import { AGENT_PREVIEW_ENABLED } from 'src/agentPreview/config'
import { auth, getFirebaseUser } from 'src/firebase'

// ----------------------------------------------------------------------
// Whether this browser already holds a console session.
//
// Asked by the Tesla callback before it starts a sign-in again. A person who
// is signed in is not locked out, and a new trip would sign in whichever Tesla
// account the browser holds in their place: the one somebody just used to
// repair a fleet's connection, say, which need not be their own. Firebase
// keeps the session per origin, so that would replace it in every tab.
//
// A console session is the claim, not the Firebase session (see
// `docs/CONSOLE_SIGN_IN.md`). A Firebase user without it is one `/backoffice`
// refuses with `CONSOLE_SESSION_REQUIRED` and `AuthProvider` is about to sign
// out, so nobody relies on it, and it counts as nobody here.
//
// Firebase reads its session back asynchronously on a page load, and a fresh
// page load is exactly what the callback is, so this waits for it. It does not
// wait for ever: `null` means it could not tell in time, or could not read the
// claim, and the caller treats that as not knowing.
// ----------------------------------------------------------------------

/** How long Firebase has to read its session back before the answer is unknown. */
export const CONSOLE_SESSION_READ_MS = 5000

/**
 * The claim only the backend puts on a console session. Spelled out here
 * because the console cannot import the backend; the spec reads
 * `beast-backend/src/areas/auth/consoleSession.ts` to hold the two together.
 */
export const CONSOLE_SESSION_CLAIM = 'bo_at'

const heldOnceRead = async (): Promise<boolean> => {
  await auth.authStateReady()

  const user = getFirebaseUser()
  if (!user) {
    return false
  }

  const { claims } = await user.getIdTokenResult()
  return claims[CONSOLE_SESSION_CLAIM] != null
}

export const consoleSessionHeld = async (
  waitMs: number = CONSOLE_SESSION_READ_MS,
): Promise<boolean | null> => {
  if (AGENT_PREVIEW_ENABLED) {
    return getFirebaseUser() !== null
  }

  let timer: ReturnType<typeof setTimeout> | undefined
  const answer = await Promise.race([
    heldOnceRead().catch(() => null),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), waitMs)
    }),
  ])
  clearTimeout(timer)

  return answer
}
